// M24 (COP-114 §7, DBC-08): pure `data-db-table` → ECharts option mapping.
// No ECharts import — the option is a plain object handed to the lazily
// loaded runtime by `db-chart-echarts.tsx`, so unit tests need no ECharts
// (and no jsdom) at all. The chart-vs-none decision is NOT made here: the
// server-computed `chart.available` list on the part is the only authority.
/** Human label for a semantic-layer member path (`customers.country` → `country`). */
function memberLabel(column) {
    const segments = column.path.split('.');
    return segments[segments.length - 1] || column.key;
}
function metricColumns(columns) {
    return columns.filter((c) => c.memberType === 'metric');
}
/** First dimension column whose `type` is in the accepted set. */
function firstDimension(columns, types) {
    return columns.find((c) => c.memberType === 'dimension' && types.includes(c.type));
}
/** Unique values of `column` across `rows`, in first-appearance order. */
function uniqueValues(rows, column) {
    const seen = new Set();
    const out = [];
    for (const row of rows) {
        const v = String(row[column.key] ?? '');
        if (seen.has(v))
            continue;
        seen.add(v);
        out.push(v);
    }
    return out;
}
function toNumber(value) {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
}
/**
 * Map a `data-db-table` part's stored columns + rows into an ECharts option
 * for one of the server-approved chart types (COP-114 §7 axis rules):
 *   - `bar` / `pareto` — x axis is the first `'string'` dimension column;
 *     pareto additionally sorts descending by the first metric and adds a
 *     cumulative-% line on a second y-axis.
 *   - `line` / `groupedBar` — x axis is the first temporal
 *     (`'date'`/`'datetime'`) dimension column; groupedBar splits the first
 *     metric into one bar series per categorical (`'string'`) dimension value
 *     across that temporal axis.
 * Series cover every metric column (groupedBar: categories × metrics).
 */
export function buildChartOption(type, columns, rows) {
    const metrics = metricColumns(columns);
    const temporal = type === 'line' || type === 'groupedBar';
    const xCol = temporal
        ? firstDimension(columns, ['date', 'datetime'])
        : firstDimension(columns, ['string']);
    // No usable axis or nothing to plot — an empty option keeps the chart
    // mount harmless (the server should never approve a type this happens for).
    if (!xCol || metrics.length === 0) {
        return {
            tooltip: { trigger: 'axis' },
            legend: { data: [] },
            xAxis: { type: 'category', data: [] },
            yAxis: [{ type: 'value' }],
            series: [],
        };
    }
    if (type === 'pareto') {
        const metric = metrics[0];
        const sorted = [...rows].sort((a, b) => toNumber(b[metric.key]) - toNumber(a[metric.key]));
        const values = sorted.map((row) => toNumber(row[metric.key]));
        const total = values.reduce((sum, v) => sum + v, 0);
        let running = 0;
        const cumulative = values.map((v) => {
            running += v;
            return total === 0 ? 0 : Math.round((running / total) * 10000) / 100;
        });
        const barName = memberLabel(metric);
        const lineName = 'Cumulative %';
        return {
            tooltip: { trigger: 'axis' },
            legend: { data: [barName, lineName] },
            xAxis: {
                type: 'category',
                data: sorted.map((row) => String(row[xCol.key] ?? '')),
            },
            yAxis: [
                { type: 'value' },
                { type: 'value', max: 100, axisLabel: { formatter: '{value}%' } },
            ],
            series: [
                { name: barName, type: 'bar', data: values },
                { name: lineName, type: 'line', data: cumulative, yAxisIndex: 1 },
            ],
        };
    }
    if (type === 'groupedBar') {
        const catCol = firstDimension(columns, ['string']);
        const xValues = uniqueValues(rows, xCol);
        // One bar series per (categorical value × metric); each series' data is
        // aligned to the temporal axis, gaps → null.
        const categories = catCol ? uniqueValues(rows, catCol) : [null];
        const series = categories.flatMap((category) => metrics.map((metric) => {
            const name = category === null
                ? memberLabel(metric)
                : metrics.length > 1
                    ? `${category} · ${memberLabel(metric)}`
                    : category;
            const data = xValues.map((x) => {
                const row = rows.find((r) => String(r[xCol.key] ?? '') === x &&
                    (catCol === undefined || String(r[catCol.key] ?? '') === category));
                return row === undefined ? null : toNumber(row[metric.key]);
            });
            return { name, type: 'bar', data };
        }));
        return {
            tooltip: { trigger: 'axis' },
            legend: { data: series.map((s) => s.name) },
            xAxis: { type: 'category', data: xValues },
            yAxis: [{ type: 'value' }],
            series,
        };
    }
    // bar / line — one series per metric over the row order as stored.
    const seriesType = type === 'line' ? 'line' : 'bar';
    const series = metrics.map((metric) => ({
        name: memberLabel(metric),
        type: seriesType,
        data: rows.map((row) => toNumber(row[metric.key])),
    }));
    return {
        tooltip: { trigger: 'axis' },
        legend: { data: series.map((s) => s.name) },
        xAxis: {
            type: 'category',
            data: rows.map((row) => String(row[xCol.key] ?? '')),
        },
        yAxis: [{ type: 'value' }],
        series,
    };
}
//# sourceMappingURL=chart-option.js.map